import React, { useCallback, useEffect, useState } from 'react';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider,
  FormControl, Grid, InputLabel, MenuItem, Pagination, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  applyLegacyReconciliation,
  fetchCorrectedBenchmark,
  fetchCorrectedBenchmarkAnalytics,
  fetchLegacyReconciliationPreview,
  fetchTradingAiCost,
  fetchTradingConfig,
  fetchTradingControl,
  fetchTradingHistory,
  fetchTradingPnlHistory,
  fetchTradingPositionDetail,
  fetchTradingPositions,
  fetchTradingSummary,
  setTradingControl,
} from '../../api/adminTrading';
import RealizedPnlChart from '../../components/admin/RealizedPnlChart';

const PAGE_COLORS = {
  ink: '#17323c', inkSoft: '#355b63', sand: '#fffaf2', mist: '#edf3f6',
  border: '#e6d9c7', muted: '#4f646f', success: '#2f7d5d', warning: '#b57422',
  danger: '#9c4f4f', accent: '#c07c32',
};

const PAGE_SIZE = 25;

function fmt(value, digits = 2) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

const formatTradingPrice = (v) => fmt(v, 8);
const formatQuantity = (v) => fmt(v, 8);
const formatMoney = (v) => fmt(v, 2);
const formatCost = (v) => fmt(v, 6);
const formatRiskMultiple = (v) => fmt(v, 3);
const formatTokenCount = (v) => fmt(v, 0);

// Capital-normalized return (%): adaptive precision so a very small non-zero value
// (e.g. 0.00001 %) is never rounded to 0. The backend value is authoritative — the
// frontend must never recompute it from P&L / equity.
function formatPercentValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const digits = abs > 0 && abs < 0.0001 ? 8 : abs > 0 && abs < 0.01 ? 6 : 2;
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits })} %`;
}

// Explicit monetary unit for the corrected BITUNIX PERPETUAL LINEAR USDT population.
const formatUsd = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))
  ? '—' : `${fmt(v, 2)} USDT`);
const formatR = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))
  ? '—' : `${fmt(v, 3)} R`);
const formatBps = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))
  ? '—' : `${fmt(v, 2)} bps`);

// Sign-driven tone/color for P&L values. Positive -> success (green), negative ->
// danger (red), zero/null/non-finite -> neutral (default). Driven by the real numeric
// sign, never by a formatted string.
export function pnlTone(value) {
  if (value === null || value === undefined || value === '') return 'default';
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return 'default';
  return n > 0 ? 'success' : 'danger';
}

export function pnlColor(value) {
  const tone = pnlTone(value);
  if (tone === 'success') return PAGE_COLORS.success;
  if (tone === 'danger') return PAGE_COLORS.danger;
  return undefined;
}

function MetricCard({ label, value, helper, tone = 'default' }) {
  const tones = {
    default: { bg: '#fff', fg: PAGE_COLORS.ink },
    accent: { bg: alpha(PAGE_COLORS.accent, 0.08), fg: PAGE_COLORS.accent },
    success: { bg: alpha(PAGE_COLORS.success, 0.1), fg: PAGE_COLORS.success },
    warning: { bg: alpha(PAGE_COLORS.warning, 0.1), fg: PAGE_COLORS.warning },
    danger: { bg: alpha(PAGE_COLORS.danger, 0.1), fg: PAGE_COLORS.danger },
  };
  const palette = tones[tone] || tones.default;
  return (
    <Paper sx={{ p: 2, borderRadius: 4, background: palette.bg, border: `1px solid ${alpha(palette.fg, 0.12)}` }}>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, color: palette.fg, mt: 0.5 }}>{value}</Typography>
      {helper ? <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.5 }}>{helper}</Typography> : null}
    </Paper>
  );
}

export default function TradingAdminPage() {
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [config, setConfig] = useState(null);
  const [aiCost, setAiCost] = useState(null);
  const [control, setControl] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [history, setHistory] = useState({ content: [], totalElements: 0, totalPages: 0, number: 0 });
  const [filters, setFilters] = useState({ canonicalAsset: '', direction: '', status: '' });
  const [appliedFilters, setAppliedFilters] = useState({ canonicalAsset: '', direction: '', status: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [benchmarkError, setBenchmarkError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsError, setAnalyticsError] = useState('');
  const [pnlHistory, setPnlHistory] = useState(null);
  const [pnlHistoryError, setPnlHistoryError] = useState('');

  const loadSummary = useCallback(async () => {
    const [s, p, c, a, ctrl] = await Promise.all([
      fetchTradingSummary(),
      fetchTradingPositions(),
      fetchTradingConfig(),
      fetchTradingAiCost(),
      fetchTradingControl(),
    ]);
    setSummary(s);
    setPositions(Array.isArray(p) ? p : []);
    setConfig(c);
    setAiCost(a);
    setControl(ctrl);
  }, []);

  // Loaded independently so a failing benchmark endpoint degrades gracefully and never
  // makes the whole Admin Trading page unusable.
  const loadBenchmark = useCallback(async () => {
    try {
      setBenchmarkError('');
      setBenchmark(await fetchCorrectedBenchmark());
    } catch (e) {
      setBenchmark(null);
      setBenchmarkError(e.message || 'Benchmark indisponible');
    }
  }, []);

  // Realized-performance analytics load independently too (graceful degradation).
  const loadAnalytics = useCallback(async () => {
    try {
      setAnalyticsError('');
      setAnalytics(await fetchCorrectedBenchmarkAnalytics());
    } catch (e) {
      setAnalytics(null);
      setAnalyticsError(e.message || 'Analytics indisponibles');
    }
  }, []);

  // General SHADOW realized P&L history loads independently (graceful degradation); it is NOT part
  // of the critical summary path and never breaks the rest of the page.
  const loadPnlHistory = useCallback(async () => {
    try {
      setPnlHistoryError('');
      setPnlHistory(await fetchTradingPnlHistory());
    } catch (e) {
      setPnlHistory(null);
      setPnlHistoryError(e.message || 'Historique P&L indisponible');
    }
  }, []);

  const loadHistory = useCallback(async (nextFilters, nextPage = 1) => {
    try {
      const payload = await fetchTradingHistory({
        ...nextFilters,
        page: Math.max(0, nextPage - 1),
        size: PAGE_SIZE,
      });
      const content = Array.isArray(payload?.content) ? payload.content : [];
      setHistory({
        content,
        totalElements: payload?.totalElements ?? content.length,
        totalPages: payload?.totalPages ?? 0,
        number: payload?.number ?? 0,
      });
    } catch (e) {
      setError(e.message || 'Erreur de chargement de l\'historique');
    }
  }, []);

  const loadLegacy = useCallback(async () => {
    try {
      setLegacy(await fetchLegacyReconciliationPreview());
    } catch (e) {
      setLegacy(null);
    }
  }, []);

  useEffect(() => {
    if (control && !control.enabled) {
      loadLegacy();
    } else {
      setLegacy(null);
    }
  }, [control, loadLegacy]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        await loadSummary();
      } catch (e) {
        setError(e.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadSummary]);

  useEffect(() => {
    loadBenchmark();
    loadAnalytics();
    loadPnlHistory();
  }, [loadBenchmark, loadAnalytics, loadPnlHistory]);

  useEffect(() => {
    loadHistory(appliedFilters, page);
  }, [appliedFilters, page, loadHistory]);

  const openDetail = async (id) => {
    try {
      setDetail(await fetchTradingPositionDetail(id));
    } catch (e) {
      setDetail({ id, error: e.message });
    }
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    setFilters({ canonicalAsset: '', direction: '', status: '' });
    setPage(1);
    setAppliedFilters({ canonicalAsset: '', direction: '', status: '' });
  };

  const changeControl = async (enabled) => {
    try {
      setError('');
      await setTradingControl(enabled, 'MANUAL');
      await loadSummary();
    } catch (e) {
      setError(e.message || 'Erreur de changement d\'état');
    }
  };

  const confirmDisable = () => {
    if (window.confirm(
      'Désactiver le trader ?\n\n'
      + '- Aucun nouveau trade ne sera créé\n'
      + '- Les positions SHADOW restent ouvertes mais gelées\n'
      + '- Aucun TP/SL ne sera simulé pendant la pause\n'
      + '- La réactivation ne rejouera pas les candles de la pause',
    )) {
      changeControl(false);
    }
  };

  const confirmEnable = () => {
    if (window.confirm(
      'Activer le trader ?\n\n'
      + '- De nouveaux signaux éligibles pourront créer des trades SHADOW\n'
      + '- Le monitoring reprend à partir d\'une nouvelle frontière temporelle sûre\n'
      + '- Les candles accumulées pendant la pause ne seront pas rejouées',
    )) {
      changeControl(true);
    }
  };

  const confirmReconcile = () => {
    if (window.confirm(
      'Réconcilier les positions legacy ?\n\n'
      + '- Ces positions SHADOW ont été créées avant l\'existence du monitoring lifecycle\n'
      + '- Elles seront administrés terminées (LEGACY_TERMINATED)\n'
      + '- Aucun replay de candles, aucun SL/TP exécuté, aucun P&L fabriqué\n'
      + '- Elles restent visibles dans l\'historique\n'
      + '- La capacité du portefeuille sera reconstruite depuis les positions actives restantes',
    )) {
      applyLegacyReconciliation()
        .then(() => { loadLegacy(); loadSummary(); })
        .catch((e) => setError(e.message || 'Erreur de réconciliation'));
    }
  };

  if (loading) {
    return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  }

  const cost = aiCost || {};
  const portfolio = summary?.portfolio || null;
  const bench = benchmark || {};
  const perf = bench.portfolioPerformance || {};
  const exec = bench.executionQuality || {};
  const samplePolicyLabel = bench.samplePolicy === 'NOT_DEFINED'
    ? 'Politique non définie'
    : (bench.samplePolicy || '—');
  const an = analytics || {};
  const dd = an.drawdown || null;
  const dq = an.dataQuality || {};

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 900 }}>Trading</Typography>
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.75) }}>
              Simulation de position et moteur de risque déterministe.
            </Typography>
          </Box>
          <Chip
            icon={<CandlestickChartRoundedIcon />}
            label="SHADOW — NO REAL ORDERS"
            sx={{ bg: alpha(PAGE_COLORS.warning, 0.25), color: '#ffe9c2', fontWeight: 800 }}
          />
        </Box>
      </Paper>

      {error ? <Typography color="error" sx={{ mb: 2 }}>{error}</Typography> : null}

      <Paper sx={{
        p: 2, borderRadius: 5, mb: 3, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
        background: control?.enabled ? alpha(PAGE_COLORS.success, 0.1) : alpha(PAGE_COLORS.danger, 0.12),
        border: `1px solid ${control?.enabled ? alpha(PAGE_COLORS.success, 0.35) : alpha(PAGE_COLORS.danger, 0.4)}`,
      }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: control?.enabled ? PAGE_COLORS.success : PAGE_COLORS.danger }}>
            Trader : {control?.enabled ? 'ENABLED' : 'DISABLED'}
          </Typography>
          <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
            {control?.enabled
              ? 'Automatisation SHADOW active.'
              : 'Automatisation en pause — les positions existantes restent ouvertes et gelées.'}
          </Typography>
          {control?.updatedAt ? (
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
              Dernière modification : {control.updatedBy || '—'} — {new Date(control.updatedAt).toLocaleString()}
              {control?.reason ? ` (${control.reason})` : ''}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant={control?.enabled ? 'contained' : 'contained'}
          color={control?.enabled ? 'error' : 'success'}
          onClick={control?.enabled ? confirmDisable : confirmEnable}
        >
          {control?.enabled ? 'Désactiver le trader' : 'Activer le trader'}
        </Button>
      </Paper>

      {legacy && legacy.legacyCount > 0 ? (
        <Paper sx={{
          p: 2, borderRadius: 5, mb: 3, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
          background: alpha(PAGE_COLORS.warning, 0.1),
          border: `1px solid ${alpha(PAGE_COLORS.warning, 0.35)}`,
        }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: PAGE_COLORS.warning }}>
              Portefeuille legacy : {legacy.legacyCount} position(s) à réconcilier
            </Typography>
            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
              Positions créées avant le monitoring lifecycle. Après réconciliation :
              {legacy.projectedActivePositions} position(s) active(s), risque {formatMoney(legacy.projectedOpenRisk)}.
            </Typography>
          </Box>
          <Button variant="contained" color="warning" onClick={confirmReconcile}>
            Réconcilier les positions legacy
          </Button>
        </Paper>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><MetricCard label="Mode" value={summary?.executionMode || '—'} helper="Aucun ordre réel" tone="warning" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Positions ouvertes" value={summary?.openPositions ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Positions clôturées" value={summary?.closedPositions ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="P&L réalisé" value={formatMoney(summary?.netPnl)} tone={pnlTone(summary?.netPnl)} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Capital initial" value={formatMoney(portfolio?.initialEquity)} helper="USD simulés" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="P&L clôturé" value={formatMoney(portfolio?.closedRealizedPnl)} tone={pnlTone(portfolio?.closedRealizedPnl)} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Réalisé (positions ouvertes)" value={formatMoney(portfolio?.openPositionRealizedPnl)} tone={pnlTone(portfolio?.openPositionRealizedPnl)} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="P&L latent" value={portfolio?.unrealizedPnl != null ? formatMoney(portfolio.unrealizedPnl) : '—'} helper={portfolio && !portfolio.markToMarketAvailable ? 'mark indisponible' : undefined} tone={pnlTone(portfolio?.unrealizedPnl)} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="P&L total" value={portfolio?.totalPnl != null ? formatMoney(portfolio.totalPnl) : '—'} tone={pnlTone(portfolio?.totalPnl)} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Equity Shadow" value={portfolio?.currentEquity != null ? formatMoney(portfolio.currentEquity) : '—'} helper="affichage — sizing fixe" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Performance" value={portfolio?.performancePercent != null ? `${formatMoney(portfolio.performancePercent)}%` : '—'} helper="vs capital configuré — indicatif (non corrigé)" tone={pnlTone(portfolio?.performancePercent)} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Candidats" value={summary?.candidates ?? 0} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Décisions TRADE" value={summary?.tradeDecisions ?? 0} tone="success" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Décisions REJECT" value={summary?.rejectDecisions ?? 0} tone="accent" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Échecs IA" value={summary?.aiFailures ?? 0} tone="danger" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Risque approuvé" value={summary?.riskApprovedPlans ?? 0} tone="success" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Risque rejeté" value={summary?.riskRejectedPlans ?? 0} tone="warning" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Non sécurisés / max" value={`${summary?.openPositions ?? 0} / ${summary?.maxUnsecuredTrades ?? '—'}`} tone="warning" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Risque ouvert" value={formatMoney(summary?.openRiskAmount)} helper={`max ${formatMoney(summary?.maxOpenRiskAmount)}`} tone="warning" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Risque ouvert %" value={summary?.openRiskPercent != null ? `${formatMoney(summary?.openRiskPercent)}%` : '—'} helper={`max ${formatMoney(summary?.maxOpenRiskPercent)}%`} tone="warning" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Capacité restante" value={formatMoney(summary?.remainingRiskAmount)} tone="success" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Budget IA" value={formatMoney(cost.budgetUsd)} helper={cost.billingPeriod} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Dépensé" value={formatMoney(cost.spentUsd)} tone="accent" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Réservé" value={formatMoney(cost.reservedUsd)} tone="warning" /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label="Restant" value={formatMoney(cost.remainingUsd)} tone={cost.remainingUsd != null && Number(cost.remainingUsd) <= 0 ? 'danger' : 'success'} /></Grid>
      </Grid>

      <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
          Évolution du P&L net réalisé
        </Typography>
        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block', mb: 1 }}>
          Séquence des clôtures SHADOW réalisées — hors mark-to-market des positions ouvertes. Unité : USDT.
        </Typography>
        {pnlHistoryError ? (
          <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, py: 2 }}>
            Historique P&L indisponible : {pnlHistoryError}
          </Typography>
        ) : !pnlHistory ? (
          <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, py: 2 }}>Chargement…</Typography>
        ) : (
          <RealizedPnlChart
            points={pnlHistory.points}
            available={pnlHistory.dataQuality?.complete === true}
            closedPositions={pnlHistory.closedPositions ?? 0}
            colors={PAGE_COLORS}
          />
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
          Benchmark SHADOW corrigé
        </Typography>
        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block', mb: 1 }}>
          Population : H4_SIGNAL_FRESHNESS_V1 + BITUNIX_DEPTH_V1 + H1_CLOSED_CANDLE_V1 · Bitunix PERPETUAL LINEAR USDT
        </Typography>
        {benchmarkError ? (
          <Typography color="error">Benchmark indisponible : {benchmarkError}</Typography>
        ) : !benchmark ? (
          <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Chargement…</Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><MetricCard label="Positions corrigées" value={fmt(bench.totalPositions, 0)} helper={`${fmt(bench.closedPositions, 0)} clôturée(s)`} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="P&L net réalisé" value={formatUsd(bench.netRealizedPnl)} tone={pnlTone(bench.netRealizedPnl)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="R cumulé" value={formatR(bench.sumR)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="R moyen" value={formatR(bench.averageR)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Profit Factor" value={fmt(bench.profitFactor, 3)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Capital de référence" value={formatUsd(perf.baseEquity)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Capital final" value={formatUsd(perf.endingEquity)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Rendement portefeuille" value={formatPercentValue(perf.returnPct)} tone={pnlTone(perf.returnPct)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Slippage moyen" value={formatBps(exec.averageEntrySlippageBps)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Slippage défavorable max" value={formatBps(exec.maxAdverseEntrySlippageBps)} /></Grid>
            <Grid item xs={6} sm={3}><MetricCard label="Échantillon" value={samplePolicyLabel} /></Grid>
            {analytics ? (
              <>
                <Grid item xs={6} sm={3}><MetricCard label="Taux de réussite" value={formatPercentValue(an.winRatePct)} /></Grid>
                <Grid item xs={6} sm={3}><MetricCard label="Drawdown max" value={dd && dd.maxRealizedDrawdownAmount != null ? `${formatUsd(dd.maxRealizedDrawdownAmount)} (${formatPercentValue(dd.maxRealizedDrawdownPct)})` : '—'} tone={dd && dd.maxRealizedDrawdownAmount != null && Number(dd.maxRealizedDrawdownAmount) > 0 ? 'danger' : 'default'} /></Grid>
                <Grid item xs={6} sm={3}><MetricCard label="Gain moyen R" value={formatR(an.averageWinningR)} /></Grid>
                <Grid item xs={6} sm={3}><MetricCard label="Perte moyenne R" value={formatR(an.averageLosingR)} /></Grid>
                <Grid item xs={6} sm={3}><MetricCard label="Meilleur R" value={formatR(an.bestR)} /></Grid>
                <Grid item xs={6} sm={3}><MetricCard label="Pire R" value={formatR(an.worstR)} /></Grid>
              </>
            ) : null}
          </Grid>
        )}
        {analytics ? (
          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block', mt: 1 }}>
            Equity réalisée : {dq.equityCurveAvailable ? `${(an.realizedEquityCurve || []).length} clôture(s) séquencée(s)` : 'indisponible (base capital non uniforme ou preuve incomplète)'} · drawdown = séquence de clôtures réalisées (non mark-to-market).
          </Typography>
        ) : analyticsError ? (
          <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 1 }}>Analytics indisponibles : {analyticsError}</Typography>
        ) : null}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink, mb: 1 }}>Positions ouvertes</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Actif', 'Sym.', 'Éch.', 'Lev.', 'Dir.', 'Pattern', 'Engine', 'TF', 'Entrée', 'SL', 'Stop eff.', 'TP1', 'TP2', 'TP3', 'TP hit', 'Qté restante', 'P&L réalisé', 'Fees', 'R', 'Ouverte le', 'Dernier candle'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 800 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.length === 0 ? (
                <TableRow><TableCell colSpan={21}>Aucune position ouverte.</TableCell></TableRow>
              ) : positions.map((p) => (
                <TableRow key={p.id} hover onClick={() => openDetail(p.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{p.canonicalAsset}</TableCell>
                  <TableCell>{p.symbol}</TableCell>
                  <TableCell>{p.exchangeCode || '—'}</TableCell>
                  <TableCell>{p.leverage != null ? `x${p.leverage}` : '—'}</TableCell>
                  <TableCell>{p.direction}</TableCell>
                  <TableCell>{p.pattern}</TableCell>
                  <TableCell>{p.engineVersion}</TableCell>
                  <TableCell>{p.timeframe}</TableCell>
                  <TableCell>{formatTradingPrice(p.entry)}</TableCell>
                  <TableCell>{formatTradingPrice(p.stopLoss)}</TableCell>
                  <TableCell>{formatTradingPrice(p.effectiveStop)}</TableCell>
                  <TableCell>{formatTradingPrice(p.tp1)}</TableCell>
                  <TableCell>{formatTradingPrice(p.tp2)}</TableCell>
                  <TableCell>{formatTradingPrice(p.tp3)}</TableCell>
                  <TableCell>{[p.tp1Hit ? 'TP1' : null, p.tp2Hit ? 'TP2' : null].filter(Boolean).join(',') || '—'}</TableCell>
                  <TableCell>{formatQuantity(p.remainingQuantity)}</TableCell>
                  <TableCell>{formatMoney(p.realizedPnl)}</TableCell>
                  <TableCell>{formatMoney(p.realizedFees)}</TableCell>
                  <TableCell>{formatRiskMultiple(p.rMultiple)}</TableCell>
                  <TableCell>{p.openedAt ? new Date(p.openedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{p.lastProcessedCandleTime ? new Date(p.lastProcessedCandleTime).toLocaleString() : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink, mb: 1 }}>Historique des trades</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <TextField size="small" label="Actif" value={filters.canonicalAsset}
            onChange={(e) => setFilters({ ...filters, canonicalAsset: e.target.value })} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Direction</InputLabel>
            <Select label="Direction" value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}>
              <MenuItem value="">Toutes</MenuItem>
              <MenuItem value="BULLISH">BULLISH</MenuItem>
              <MenuItem value="BEARISH">BEARISH</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Statut</InputLabel>
            <Select label="Statut" value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">Tous</MenuItem>
              {['CLOSED_TP', 'CLOSED_SL', 'CANCELLED', 'INVALIDATED', 'ERROR'].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={applyFilters}>Rechercher</Button>
          <Button variant="outlined" onClick={resetFilters}>Réinitialiser</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Actif', 'Dir.', 'Pattern', 'Engine', 'TF', 'Entrée', 'Sortie', 'Raison', 'P&L net', 'R', 'Ouverte', 'Fermée', 'Statut'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 800 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.content.length === 0 ? (
                <TableRow><TableCell colSpan={13}>Aucun trade clôturé.</TableCell></TableRow>
              ) : history.content.map((h) => (
                <TableRow key={h.id} hover onClick={() => openDetail(h.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{h.canonicalAsset}</TableCell>
                  <TableCell>{h.direction}</TableCell>
                  <TableCell>{h.pattern}</TableCell>
                  <TableCell>{h.engineVersion}</TableCell>
                  <TableCell>{h.timeframe}</TableCell>
                  <TableCell>{formatTradingPrice(h.entry)}</TableCell>
                  <TableCell>{formatTradingPrice(h.exit)}</TableCell>
                  <TableCell>{h.exitReason || '—'}</TableCell>
                  <TableCell sx={{ color: pnlColor(h.netPnl), fontWeight: 700 }}>{formatMoney(h.netPnl)}</TableCell>
                  <TableCell>{formatRiskMultiple(h.rMultiple)}</TableCell>
                  <TableCell>{h.openedAt ? new Date(h.openedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{h.closedAt ? new Date(h.closedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{h.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Pagination
            page={Math.min(page, Math.max(history.totalPages, 1))}
            count={Math.max(history.totalPages, 1)}
            color="primary"
            onChange={(_, p) => setPage(p)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink, mb: 1 }}>Coût IA ({cost.billingPeriod})</Typography>
        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
          Budget {formatMoney(cost.budgetUsd)} · Dépensé {formatMoney(cost.spentUsd)} · Réservé {formatMoney(cost.reservedUsd)} · Restant {formatMoney(cost.remainingUsd)}
        </Typography>
        {Array.isArray(cost.recentUsage) && cost.recentUsage.length > 0 && (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Date', 'Model', 'Statut', 'Input', 'Cached', 'Output', 'Coût', 'Erreur'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 800 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {cost.recentUsage.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.requestTimestamp ? new Date(u.requestTimestamp).toLocaleString() : '—'}</TableCell>
                    <TableCell>{u.model}</TableCell>
                    <TableCell>{u.status}</TableCell>
                    <TableCell>{formatTokenCount(u.inputTokens)}</TableCell>
                    <TableCell>{formatTokenCount(u.cachedInputTokens)}</TableCell>
                    <TableCell>{formatTokenCount(u.outputTokens)}</TableCell>
                    <TableCell>{formatCost(u.actualCostUsd)}</TableCell>
                    <TableCell>{u.errorCategory || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink, mb: 1 }}>Configuration risque (lecture seule)</Typography>
        {config ? (
          <Grid container spacing={1}>
            {[
              ['Risk / trade %', config.riskPerTradePercent],
              ['Equity SHADOW', config.shadowEquityUsd],
              ['Notional max', config.maxPositionNotionalUsd],
              ['TP1 %', config.tp1AllocationPercent],
              ['TP2 %', config.tp2AllocationPercent],
              ['TP3 %', config.tp3AllocationPercent],
              ['Break-even après TP1', config.breakEvenAfterTp1 ? 'Oui' : 'Non'],
              ['RR minimum', config.minRiskReward],
              ['Fees (bps)', config.feeRateBps],
              ['Trades non sécurisés max', config.maxUnsecuredTrades],
              ['Risque ouvert total max %', config.maxTotalOpenRiskPct],
              ['Levier par défaut', config.defaultLeverage != null ? `x${config.defaultLeverage}` : '—'],
              ['Levier max', config.maxLeverage != null ? `x${config.maxLeverage}` : '—'],
              ['Short autorisé', config.allowShort ? 'Oui' : 'Non'],
            ].map(([label, value]) => (
              <Grid item xs={6} sm={4} key={label}>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{label}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{fmt(value)}</Typography>
              </Grid>
            ))}
          </Grid>
        ) : <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Configuration indisponible.</Typography>}
      </Paper>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>Détail position SHADOW</DialogTitle>
        <DialogContent>
          {detail?.error ? (
            <Typography color="error">{detail.error}</Typography>
          ) : detail ? (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {detail.canonicalAsset} — {detail.direction} ({detail.status})
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                {[
                  ['Entrée', detail.lineage?.entry, formatTradingPrice],
                  ['Stop', detail.lineage?.stopLoss, formatTradingPrice],
                  ['TP1', detail.lineage?.tp1, formatTradingPrice],
                  ['TP2', detail.lineage?.tp2, formatTradingPrice],
                  ['TP3', detail.lineage?.tp3, formatTradingPrice],
                  ['Stop effectif', detail.effectiveStop, formatTradingPrice],
                  ['TP1 touché', detail.tp1Hit ? 'Oui' : 'Non', (v) => v],
                  ['TP2 touché', detail.tp2Hit ? 'Oui' : 'Non', (v) => v],
                  ['Alloc. TP1 %', detail.lineage?.tp1AllocationPercent, (v) => (v != null ? `${fmt(v)}%` : '—')],
                  ['Alloc. TP2 %', detail.lineage?.tp2AllocationPercent, (v) => (v != null ? `${fmt(v)}%` : '—')],
                  ['Alloc. TP3 %', detail.lineage?.tp3AllocationPercent, (v) => (v != null ? `${fmt(v)}%` : '—')],
                  ['Qté planifiée', detail.lineage?.plannedQuantity, formatQuantity],
                  ['Notionnel', detail.lineage?.plannedNotional, formatMoney],
                  ['Risque', detail.lineage?.riskAmount, formatMoney],
                  ['Risque %', detail.lineage?.riskPercent, (v) => (v != null ? `${fmt(v)}%` : '—')],
                  ['Levier', detail.leverage != null ? `x${detail.leverage}` : '—', (v) => v],
                  ['Levier max', detail.lineage?.maxLeverage != null ? `x${detail.lineage.maxLeverage}` : '—', (v) => v],
                  ['Marge initiale', detail.initialMargin, formatMoney],
                  ['Frais d\'entrée', detail.entryFee, formatMoney],
                  ['Frais (bps)', detail.lineage?.feeRateBps, (v) => fmt(v, 4)],
                  ['Échange', detail.exchangeCode, (v) => v ?? '—'],
                  ['Marché', detail.marketSymbol, (v) => v ?? '—'],
                  ['RR', detail.lineage?.riskRewardRatio, formatRiskMultiple],
                  ['Décision IA', detail.lineage?.aiDecision, (v) => v ?? '—'],
                  ['Confiance IA', detail.lineage?.confidence, (v) => fmt(v, 3)],
                  ['Entrée proposée IA', detail.lineage?.aiProposedEntry, formatTradingPrice],
                  ['Stop proposé IA', detail.lineage?.aiProposedStopLoss, formatTradingPrice],
                  ['Cible proposée IA', detail.lineage?.aiProposedTakeProfit, formatTradingPrice],
                  ['Statut plan', detail.lineage?.planStatus, (v) => v ?? '—'],
                  ['Raison rejet', detail.lineage?.rejectionReason || '—', (v) => v],
                  ['P&L réalisé', detail.realizedPnl, formatMoney],
                  ['Fees', detail.realizedFees, formatMoney],
                  ['Frais entrée alloués', detail.allocatedEntryFee, formatMoney],
                  ['Frais de sortie', detail.exitFee, formatMoney],
                  ['Prix de sortie', detail.exitPrice, formatTradingPrice],
                  ['Raison de clôture', detail.closeReason, (v) => v ?? '—'],
                  ['Fermée le', detail.closedAt ? new Date(detail.closedAt).toLocaleString() : '—', (v) => v],
                  ['Monitoring start', detail.monitoringStartedAt ? new Date(detail.monitoringStartedAt).toLocaleString() : '—', (v) => v],
                  ['R multiple', detail.rMultiple, formatRiskMultiple],
                  ['Capital de référence', detail.portfolioEquityAtEntry, (v) => (v != null && Number.isFinite(Number(v)) ? `${fmt(v, 2)} USDT` : '—')],
                  ['Impact portefeuille', detail.portfolioReturnPct, (v) => (v != null && v !== '' ? formatPercentValue(v) : '—')],
                ].map(([label, value, formatFn]) => (
                  <Grid item xs={6} sm={4} key={label}>
                    <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{label}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{formatFn(value)}</Typography>
                  </Grid>
                ))}
              </Grid>
              {detail.lineage?.aiReasons?.length ? (
                <>
                  <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, mt: 1 }}>Raisons IA</Typography>
                  <Typography variant="body2">{detail.lineage.aiReasons.join(' · ')}</Typography>
                </>
              ) : null}
              {detail.events?.length ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>Événements</Typography>
                  {detail.events.map((e, i) => (
                    <Typography key={i} variant="body2">
                      {e.eventType} — {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'} {e.reason ? `(${e.reason})` : ''}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
